import { createApi } from "@reduxjs/toolkit/query/react";
import { API_BASE_URL } from "../../utils/constants";
import { axiosBaseQuery } from "../baseQuery";
import reportsParser from "../parser/reportsParser";
import {
  FetchParameterWiseReportRequest,
  ParsedFetchParameterWiseResponse,
  FetchScoreWiseReportRequest,
  ParsedFetchScoreWiseResponse,
  FetchMedalWiseReportRequest,
  ParsedFetchMedalWiseResponse,
  FetchTargetVsAchievementReportRequest,
  ParsedFetchTargetVsAchievementResponse,
  FetchAttendanceReportRequest,
  ParsedFetchAttendanceResponse,
  FetchEnrolmentReportRequest,
  ParsedFetchEnrolmentResponse,
  FetchDropOutReportRequest,
  ParsedFetchDropOutResponse,
  FetchPlacementReportRequest,
  ParsedFetchPlacementResponse,
  FetchDemandDrivenPlacementReportRequest,
  ParsedFetchDemandDrivenPlacementResponse,
  FetchRetentionReportRequest,
  ParsedFetchRetentionResponse,
  FetchOperationsFinanceReportRequest,
  ParsedFetchOperationsFinanceResponse,
  FetchOperationsHRReportRequest,
  ParsedFetchOperationsHRResponse,
  FetchGovernanceAndEthicsReportRequest,
  ParsedFetchGovernanceAndEthicsResponse,
  FetchSessionQualityReportRequest,
  ParsedFetchSessionQualityResponse,
  FetchStakeholdersFeedbackReportRequest,
  ParsedFetchStakeholdersFeedbackResponse,
  FetchCenterVisitBrandingReportRequest,
  ParsedFetchCenterVisitBrandingResponse,
  FetchStaffTrainingReportRequest,
  ParsedFetchStaffTrainingResponse,
  ParsedFetchAttendanceSummaryResponse,
  FetchedAttendanceSummaryReportRequest,
} from "../types/reports";

export const reportApi = createApi({
  reducerPath: "reportApi",
  baseQuery: axiosBaseQuery({ baseUrl: API_BASE_URL }),
  tagTypes: [
    "ParameterWise",
    "ScoreWise",
    "MedalWise",
    "TargetVsAchievement",
    "Attendence",
    "Enrolment",
    "DropOut",
    "Placement",
    "DemandDrivenPlacement",
    "Retention",
    "OperationsFinance",
    "OperationsHR",
    "GovernanceAndEthics",
    "SessionQuality",
    "StakeholdersFeedback",
    "CenterVisitBranding",
    "StaffTraining",
    "AttendenceSummaryReports",
    "ManageAward",
  ],
  endpoints: (builder) => ({
    fetchParameterWiseReports: builder.query<
      ParsedFetchParameterWiseResponse,
      FetchParameterWiseReportRequest
    >({
      query: (params) => ({
        url: "/report",
        method: "GET",
        params,
      }),

      providesTags: ["ParameterWise"],
      transformResponse: (response: any) =>
        reportsParser.fetchedParameterWise(response),
    }),
    fetchScoreWiseReports: builder.query<
      ParsedFetchScoreWiseResponse,
      FetchScoreWiseReportRequest
    >({
      query: (params) => ({
        url: "/report/score-wise-report",
        method: "GET",
        params,
      }),
      providesTags: ["ScoreWise"],
      transformResponse: (response: any) =>
        reportsParser.fetchedScoreWise(response),
    }),
    fetchMedalWiseReports: builder.query<
      ParsedFetchMedalWiseResponse,
      FetchMedalWiseReportRequest
    >({
      query: (params) => ({
        url: "/report/medal-records",
        method: "GET",
        params,
      }),
      providesTags: ["MedalWise"],
      transformResponse: (response: any) =>
        reportsParser.fetchedMedalWise(response),
    }),
    fetchTargetVsAchievementReports: builder.query<
      ParsedFetchTargetVsAchievementResponse,
      FetchTargetVsAchievementReportRequest
    >({
      query: (params) => ({
        url: "/report/target-vs-achievement",
        method: "GET",
        params,
      }),
      providesTags: ["TargetVsAchievement"],
      transformResponse: (response: any) =>
        reportsParser.fetchedTargetVsAchievement(response),
    }),
    fetchAttendanceReports: builder.query<
      ParsedFetchAttendanceResponse,
      FetchAttendanceReportRequest
    >({
      query: (params) => ({
        url: "/report/attendence-records",
        method: "GET",
        params,
      }),
      providesTags: ["Attendence"],
      transformResponse: (response: any) =>
        reportsParser.fetchedAttendance(response),
    }),
    fetchAttendanceSummaryReports: builder.query<
      ParsedFetchAttendanceSummaryResponse,
      FetchedAttendanceSummaryReportRequest
    >({
      query: (params) => ({
        url: "/report/attendence-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["AttendenceSummaryReports"],
      transformResponse: (response: any) =>
        reportsParser.fetchedAttendanceSummaryReports(response),
    }),
    fetchEnrolmentReports: builder.query<
      ParsedFetchEnrolmentResponse,
      FetchEnrolmentReportRequest
    >({
      query: (params) => ({
        url: "/report/enrolment-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["Enrolment"],
      transformResponse: (response: any) =>
        reportsParser.fetchedEnrolment(response),
    }),
    fetchDropOutReports: builder.query<
      ParsedFetchDropOutResponse,
      FetchDropOutReportRequest
    >({
      query: (params) => ({
        url: "/report/drop-out-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["DropOut"],
      transformResponse: (response: any) =>
        reportsParser.fetchedDropOut(response),
    }),
    fetchPlacementReports: builder.query<
      ParsedFetchPlacementResponse,
      FetchPlacementReportRequest
    >({
      query: (params) => ({
        url: "/report/placement-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["Placement"],
      transformResponse: (response: any) =>
        reportsParser.fetchedPlacement(response),
    }),
    fetchDemandDrivenPlacementReports: builder.query<
      ParsedFetchDemandDrivenPlacementResponse,
      FetchDemandDrivenPlacementReportRequest
    >({
      query: (params) => ({
        url: "/report/demand-driven-placement-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["DemandDrivenPlacement"],
      transformResponse: (response: any) =>
        reportsParser.fetchedDemandDrivenPlacement(response),
    }),
    fetchRetentionReports: builder.query<
      ParsedFetchRetentionResponse,
      FetchRetentionReportRequest
    >({
      query: (params) => ({
        url: "/report/retention-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["Retention"],
      transformResponse: (response: any) =>
        reportsParser.fetchedRetention(response),
    }),
    fetchOperationsFinanceReports: builder.query<
      ParsedFetchOperationsFinanceResponse,
      FetchOperationsFinanceReportRequest
    >({
      query: (params) => ({
        url: "/report/finance-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["OperationsFinance"],
      transformResponse: (response: any) =>
        reportsParser.fetchedOperationsFinance(response),
    }),
    fetchOperationsHRReports: builder.query<
      ParsedFetchOperationsHRResponse,
      FetchOperationsHRReportRequest
    >({
      query: (params) => ({
        url: "/report/hr-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["OperationsHR"],
      transformResponse: (response: any) =>
        reportsParser.fetchedOperationsHR(response),
    }),
    fetchGovernanceAndEthicsReports: builder.query<
      ParsedFetchGovernanceAndEthicsResponse,
      FetchGovernanceAndEthicsReportRequest
    >({
      query: (params) => ({
        url: "/report/governance-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["GovernanceAndEthics"],
      transformResponse: (response: any) =>
        reportsParser.fetchedGovernanceAndEthics(response),
    }),
    fetchSessionQualityReports: builder.query<
      ParsedFetchSessionQualityResponse,
      FetchSessionQualityReportRequest
    >({
      query: (params) => ({
        url: "/report/session-quality-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["SessionQuality"],
      transformResponse: (response: any) =>
        reportsParser.fetchedSessionQuality(response),
    }),
    fetchStakeholdersFeedbackReports: builder.query<
      ParsedFetchStakeholdersFeedbackResponse,
      FetchStakeholdersFeedbackReportRequest
    >({
      query: (params) => ({
        url: "/report/stakeholder-feedback-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["StakeholdersFeedback"],
      transformResponse: (response: any) =>
        reportsParser.fetchedStakeholdersFeedback(response),
    }),
    fetchCenterVisitBrandingReports: builder.query<
      ParsedFetchCenterVisitBrandingResponse,
      FetchCenterVisitBrandingReportRequest
    >({
      query: (params) => ({
        url: "/report/centre-visit-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["CenterVisitBranding"],
      transformResponse: (response: any) =>
        reportsParser.fetchedCenterVisitBranding(response),
    }),
    fetchStaffTrainingReports: builder.query<
      ParsedFetchStaffTrainingResponse,
      FetchStaffTrainingReportRequest
    >({
      query: (params) => ({
        url: "/report/staff-training-summary-records",
        method: "GET",
        params,
      }),
      providesTags: ["StaffTraining"],
      transformResponse: (response: any) =>
        reportsParser.fetchedStaffTraining(response),
    }),
    fetchManageAward: builder.query<any, void>({
      query: () => ({
        url: "/manage-award",
        method: "GET",
      }),
      providesTags: ["ManageAward"],
      transformResponse: (response: any) => reportsParser.parseManageAward(response),
    }),
  }),
});

// Export hooks for all the endpoints
export const {
  useFetchParameterWiseReportsQuery,
  useFetchScoreWiseReportsQuery,
  useFetchMedalWiseReportsQuery,
  useFetchTargetVsAchievementReportsQuery,
  useFetchAttendanceReportsQuery,
  useFetchAttendanceSummaryReportsQuery,
  useFetchEnrolmentReportsQuery,
  useFetchDropOutReportsQuery,
  useFetchPlacementReportsQuery,
  useFetchDemandDrivenPlacementReportsQuery,
  useFetchRetentionReportsQuery,
  useFetchOperationsFinanceReportsQuery,
  useFetchOperationsHRReportsQuery,
  useFetchGovernanceAndEthicsReportsQuery,
  useFetchSessionQualityReportsQuery,
  useFetchStakeholdersFeedbackReportsQuery,
  useFetchCenterVisitBrandingReportsQuery,
  useFetchStaffTrainingReportsQuery,
  useFetchManageAwardQuery
} = reportApi;
